// components/journey-surface/journey-nodes.js
// ═══════════════════════════════════════════════
// SVG node and edge renderers for the journey map radial mindmap.

import React from 'react';
import { NODE_TYPES, NODE_COLORS, FILE_NODE_COLORS, NODE_STATUS } from '../../lib/workspace-config';
import { NODE_CIRCLE_RADIUS, NODE_RECT_WIDTH, NODE_RECT_HEIGHT } from './journey-layout';

// ── Node renderer ──────────────────────────────────────────
// Each node renders ONLY its short label inside the shape (M1, Q2, F1).
// Full titles live in the legend below the SVG so long descriptions
// never overlap circles or sibling nodes.
export function NodeElement({ node, label, onClick, onHover, healthLensActive, isFocused }) {
  const isFile = node.type === NODE_TYPES.FILE;
  const color = isFile
    ? (FILE_NODE_COLORS[node.file_source] || FILE_NODE_COLORS.uploaded)
    : (NODE_COLORS[node.type] || '#888');

  const isActive = node.status === NODE_STATUS.ACTIVE;
  const isBuilding = node.status === NODE_STATUS.BUILDING;
  const isReferenced = node.status === NODE_STATUS.REFERENCED;
  const isRelated = node.status === NODE_STATUS.RELATED;
  const isError = node.status === NODE_STATUS.ERROR;
  const isFuture = node.status === NODE_STATUS.FUTURE;

  // ── Health lens treatment ──
  // When the Health lens is active, fade nodes by age:
  //   < 7 days   → fresh, full opacity
  //   7-30 days  → aging, 60% opacity
  //   > 30 days  → stale, 30% opacity + dashed border
  // Root nodes are exempt — they represent the workspace itself, not
  // a moment to be aged out. File nodes use the same fade since old
  // unused files are noise on the map.
  let healthOpacity = 1;
  let healthStale = false;
  if (healthLensActive && node.type !== 'root') {
    const createdMs = node.created_at ? new Date(node.created_at).getTime() : 0;
    if (createdMs > 0) {
      const ageDays = (Date.now() - createdMs) / (1000 * 60 * 60 * 24);
      if (ageDays > 30) {
        healthOpacity = 0.3;
        healthStale = true;
      } else if (ageDays > 7) {
        healthOpacity = 0.6;
      }
    }
  }

  // Combine health fade with the existing status fade. Health takes
  // precedence when active because it's a deliberate user gesture.
  const baseOpacity = isFuture ? 0.45 : (node.status === NODE_STATUS.PAST ? 0.7 : 1);
  const opacity = healthLensActive ? healthOpacity : baseOpacity;
  // Stale nodes get a dashed border even if they would otherwise be solid.
  const stroke_dash = (isFuture || healthStale) ? '3,3' : undefined;

  if (isFile) {
    // File node — rounded rectangle with the label centered
    const x = node.x - NODE_RECT_WIDTH / 2;
    const y = node.y - NODE_RECT_HEIGHT / 2;
    return (
      <g
        className="wsmap-node"
        style={{ cursor: 'pointer', opacity }}
        onClick={() => onClick?.(node)}
        onMouseEnter={() => onHover?.(node)}
        onMouseLeave={() => onHover?.(null)}
      >
        {/* Focus or building outer ring */}
        {(isFocused || isBuilding) && (
          <rect
            x={x - 4} y={y - 4}
            width={NODE_RECT_WIDTH + 8} height={NODE_RECT_HEIGHT + 8}
            rx={6}
            fill="none"
            stroke={isBuilding ? '#c8a96e' : '#c8a96e'}
            strokeWidth={1.2}
            opacity={isBuilding ? 0.4 : 0.5}
          >
            {isBuilding && (
              <animate attributeName="opacity" values="0.15;0.5;0.15" dur="1.5s" repeatCount="indefinite" />
            )}
          </rect>
        )}
        <rect
          x={x} y={y}
          width={NODE_RECT_WIDTH} height={NODE_RECT_HEIGHT}
          rx={4}
          fill={isBuilding ? 'rgba(200,169,110,0.12)' :
                isError ? 'rgba(196,96,80,0.08)' :
                isReferenced ? 'rgba(155,143,196,0.08)' :
                isRelated ? 'rgba(122,158,130,0.08)' :
                node.file_source === 'generated' ? 'rgba(200,169,110,0.08)' : '#141414'}
          stroke={isFocused ? '#c8a96e' :
                  isBuilding ? '#c8a96e' :
                  isError ? '#c46050' :
                  isReferenced ? '#9b8fc4' :
                  isRelated ? '#7a9e82' : color}
          strokeWidth={isFocused ? 2 : (isActive || isBuilding ? 1.6 : isError ? 1.4 : isReferenced || isRelated ? 1.2 : 0.9)}
          strokeDasharray={stroke_dash}
        />
        <text
          x={node.x}
          y={node.y + 4}
          textAnchor="middle"
          fontSize={11}
          fontWeight={600}
          fontFamily="Georgia, serif"
          fill={color}
          style={{ pointerEvents: 'none', letterSpacing: '0.04em' }}
        >
          {label}
        </text>
      </g>
    );
  }

  // Moment node — circle with label centered
  return (
    <g
      className="wsmap-node"
      style={{ cursor: 'pointer', opacity }}
      onClick={() => onClick?.(node)}
    >
      {(isActive || isBuilding) && (
        <circle
          cx={node.x} cy={node.y}
          r={NODE_CIRCLE_RADIUS + 5}
          fill="none"
          stroke={isBuilding ? '#c8a96e' : color}
          strokeWidth={0.8}
          opacity={0.25}
        >
          {isBuilding && (
            <animate attributeName="opacity" values="0.1;0.4;0.1" dur="1.5s" repeatCount="indefinite" />
          )}
        </circle>
      )}
      <circle
        cx={node.x} cy={node.y}
        r={NODE_CIRCLE_RADIUS}
        fill={isError ? 'rgba(196,96,80,0.12)' :
              isReferenced ? 'rgba(155,143,196,0.12)' :
              isRelated ? 'rgba(122,158,130,0.12)' : `${color}18`}
        stroke={isError ? '#c46050' : isRelated ? '#7a9e82' : color}
        strokeWidth={isActive ? 1.6 : isError ? 1.4 : 1}
        strokeDasharray={stroke_dash}
      />
      <text
        x={node.x}
        y={node.y + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fontFamily="Georgia, serif"
        fill={color}
        style={{ pointerEvents: 'none', letterSpacing: '0.04em' }}
      >
        {label}
      </text>
    </g>
  );
}

// ── Edge renderer ──────────────────────────────────────────
export function EdgeElement({ from, to }) {
  if (!from || !to) return null;
  return (
    <line
      x1={from.x}
      y1={from.y}
      x2={to.x}
      y2={to.y}
      stroke="#2a2a2a"
      strokeWidth={0.9}
    />
  );
}
